/**
 * Related Searches Template Module
 *
 * Displays clickable pill-style links for related search terms.
 * Commonly used on e-commerce search results pages.
 */

import React, { useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { TemplateModule, TemplateConfig, PreviewProps } from '../../types';
import { getPadding } from '../../../previewHelpers';

// ============================================================================
// TEMPLATE DEFINITION
// ============================================================================

const definition = {
  id: 'related-searches',
  name: 'Related Searches',
  category: 'search' as const,
  description: 'Clickable pill-style links for related search terms',
  tags: ['Search', 'E-commerce', 'Navigation', 'Keywords'],
  fields: [
    { name: 'showHeading', label: 'Show Heading', type: 'checkbox' as const, default: true },
    { name: 'headingText', label: 'Heading Text', type: 'text' as const, default: 'Related Searches' },
    {
      name: 'headingStyle',
      label: 'Heading Style',
      type: 'select' as const,
      options: ['simple', 'with-icon', 'minimal'],
      default: 'with-icon',
    },
    {
      name: 'pillStyle',
      label: 'Pill Style',
      type: 'select' as const,
      options: ['filled', 'outlined', 'ghost'],
      default: 'outlined',
    },
    {
      name: 'pillSize',
      label: 'Pill Size',
      type: 'select' as const,
      options: ['small', 'medium', 'large'],
      default: 'medium',
    },
    { name: 'pillColor', label: 'Pill Color', type: 'color' as const, default: '#2563eb' },
    {
      name: 'textColor',
      label: 'Text Color (Filled)',
      type: 'color' as const,
      default: '#ffffff',
      hint: 'Text color for filled style',
    },
    {
      name: 'hoverStyle',
      label: 'Hover Effect',
      type: 'select' as const,
      options: ['darken', 'lighten', 'invert'],
      default: 'darken',
    },
    {
      name: 'showSearchIcon',
      label: 'Show Search Icon',
      type: 'checkbox' as const,
      default: false,
      hint: 'Add search icon to each pill',
    },
    { name: 'layout', label: 'Layout', type: 'select' as const, options: ['wrap', 'scroll', 'grid'], default: 'wrap' },
    {
      name: 'columns',
      label: 'Grid Columns',
      type: 'number' as const,
      default: 3,
      min: 2,
      max: 6,
      hint: 'Only used with grid layout',
    },
    { name: 'maxPills', label: 'Max Pills', type: 'number' as const, default: 10, min: 3, max: 20 },
    {
      name: 'pillsPerRow',
      label: 'Pills Per Row (Initial)',
      type: 'number' as const,
      default: 5,
      min: 2,
      max: 10,
      hint: 'Pills to show before View More',
    },
    { name: 'expandable', label: 'Expandable (View More)', type: 'checkbox' as const, default: false },
    { name: 'viewMoreText', label: 'View More Text', type: 'text' as const, default: 'Show More' },
    {
      name: 'borderRadius',
      label: 'Border Radius',
      type: 'select' as const,
      options: ['square', 'rounded', 'pill'],
      default: 'pill',
    },
    { name: 'backgroundColor', label: 'Background', type: 'color' as const, default: '#f8fafc' },
    {
      name: 'containerPadding',
      label: 'Container Padding',
      type: 'select' as const,
      options: ['none', 'sm', 'md', 'lg', 'custom'],
      default: 'md',
    },
    {
      name: 'customPadding',
      label: 'Custom Padding',
      type: 'text' as const,
      default: '',
      hint: 'CSS padding value, e.g. 10px 20px',
      showWhen: { field: 'containerPadding', value: 'custom' },
    },
    { name: 'customCssClass', label: 'Custom CSS Class', type: 'text' as const, default: '' },
  ],
};

// ============================================================================
// SAMPLE DATA
// ============================================================================

const sampleData = {
  searches: [
    'mens suits',
    'mens black suits',
    'mens khakis',
    'mens dress shirts',
    'mens blazers',
    'mens ties',
    'formal wear',
    'business casual',
    'slim fit suits',
    'wedding suits',
  ],
};

// ============================================================================
// SHARED HELPERS
// ============================================================================

const COLORS = {
  navy: '#1e3a5f',
};

const sizeMap: Record<string, { padding: string; fontSize: string; galleryFontSize: string; iconSize: number }> = {
  small: { padding: '6px 12px', fontSize: '0.75rem', galleryFontSize: '0.5rem', iconSize: 12 },
  medium: { padding: '8px 16px', fontSize: '0.8rem', galleryFontSize: '0.6rem', iconSize: 14 },
  large: { padding: '12px 20px', fontSize: '0.9rem', galleryFontSize: '0.7rem', iconSize: 16 },
};

const radiusMap: Record<string, number> = {
  square: 4,
  rounded: 8,
  pill: 50,
};

const adjustColor = (color: string, amount: number): string => {
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const SearchIconSvg = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ flexShrink: 0 }}
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

// ============================================================================
// PREVIEW COMPONENT
// ============================================================================

const Preview: React.FC<PreviewProps> = ({ config, isGalleryPreview = false, expandedLists = {}, toggleExpand }) => {
  const c = config;
  const maxPills = Math.min((c.maxPills as number) || 10, 10);
  const pillsPerRow = (c.pillsPerRow as number) || 5;
  const isExpandable = Boolean(c.expandable);
  const viewMoreText = (c.viewMoreText as string) || 'Show More';
  const pillColor = (c.pillColor as string) || '#2563eb';
  const textColor = (c.textColor as string) || '#ffffff';
  const backgroundColor = (c.backgroundColor as string) || '#f8fafc';
  const hoverStyle = (c.hoverStyle as string) || 'darken';

  const isExpanded = expandedLists['related-searches'];
  const visiblePills = isExpanded || !isExpandable ? maxPills : Math.min(pillsPerRow, maxPills);
  const hasMorePills = maxPills > pillsPerRow;

  const size = sizeMap[(c.pillSize as string) || 'medium'] || sizeMap.medium;
  const radius = radiusMap[(c.borderRadius as string) || 'pill'] || radiusMap.pill;

  const getPillStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      padding: size.padding,
      fontSize: isGalleryPreview ? size.galleryFontSize : size.fontSize,
      borderRadius: radius,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      textDecoration: 'none',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    };

    switch (c.pillStyle) {
      case 'filled':
        return { ...base, backgroundColor: pillColor, color: textColor, border: 'none' };
      case 'ghost':
        return { ...base, backgroundColor: 'transparent', color: pillColor, border: 'none' };
      case 'outlined':
      default:
        return { ...base, backgroundColor: 'transparent', color: pillColor, border: `1.5px solid ${pillColor}` };
    }
  };

  const getHoverStyles = (baseStyles: React.CSSProperties): React.CSSProperties => {
    switch (hoverStyle) {
      case 'darken':
        if (c.pillStyle === 'filled') {
          return { ...baseStyles, backgroundColor: adjustColor(pillColor, -30) };
        }
        return { ...baseStyles, backgroundColor: adjustColor(pillColor, -30), color: textColor };
      case 'lighten':
        if (c.pillStyle === 'filled') {
          return { ...baseStyles, backgroundColor: adjustColor(pillColor, 30) };
        }
        return { ...baseStyles, backgroundColor: `${pillColor}15` };
      case 'invert':
        if (c.pillStyle === 'filled') {
          return {
            ...baseStyles,
            backgroundColor: 'transparent',
            color: pillColor,
            border: `1.5px solid ${pillColor}`,
          };
        }
        return { ...baseStyles, backgroundColor: pillColor, color: textColor, border: 'none' };
      default:
        return baseStyles;
    }
  };

  const getContainerStyles = (): React.CSSProperties => {
    switch (c.layout) {
      case 'scroll':
        return { display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' };
      case 'grid':
        return { display: 'grid', gridTemplateColumns: `repeat(${c.columns || 3}, 1fr)`, gap: '8px' };
      default:
        return { display: 'flex', flexWrap: 'wrap', gap: '8px' };
    }
  };

  const getHeadingStyles = (): React.CSSProperties => {
    switch (c.headingStyle) {
      case 'minimal':
        return {
          margin: 0,
          fontSize: isGalleryPreview ? '0.6rem' : '0.8rem',
          fontWeight: 500,
          color: '#666',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        };
      default:
        return {
          margin: 0,
          fontSize: isGalleryPreview ? '0.7rem' : '0.9rem',
          fontWeight: 600,
          color: COLORS.navy,
        };
    }
  };

  // Pill component with hover handling
  const Pill: React.FC<{ term: string }> = ({ term }) => {
    const [isHovered, setIsHovered] = useState(false);
    const baseStyles = getPillStyles();
    const currentStyles = isHovered ? getHoverStyles(baseStyles) : baseStyles;

    return (
      <a
        href="#"
        onClick={(e) => e.preventDefault()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          ...currentStyles,
          ...(c.layout === 'grid' ? { justifyContent: 'center', textAlign: 'center' } : {}),
        }}
      >
        {c.showSearchIcon && <SearchIconSvg size={size.iconSize} />}
        <span>{term}</span>
      </a>
    );
  };

  return (
    <Box
      className={(c.customCssClass as string) || undefined}
      style={{
        backgroundColor,
        padding: isGalleryPreview
          ? '12px'
          : c.containerPadding === 'custom' && c.customPadding
          ? (c.customPadding as string)
          : getPadding((c.containerPadding as string) || 'md'),
        borderRadius: '8px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {c.showHeading && (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: isGalleryPreview ? '10px' : '14px',
          }}
        >
          {c.headingStyle === 'with-icon' && <SearchIconSvg size={isGalleryPreview ? 14 : 18} />}
          <Typography component="h4" style={getHeadingStyles()}>
            {(c.headingText as string) || 'Related Searches'}
          </Typography>
          {isExpandable && hasMorePills && !isGalleryPreview && toggleExpand && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                toggleExpand('related-searches');
              }}
              style={{
                marginLeft: 'auto',
                fontSize: '0.8rem',
                color: pillColor,
                textDecoration: 'none',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {isExpanded ? '← Show Less' : `${viewMoreText} (${maxPills - pillsPerRow} more)`}
            </a>
          )}
        </Box>
      )}
      <Box style={getContainerStyles()}>
        {sampleData.searches.slice(0, visiblePills).map((term, index) => (
          <Pill key={index} term={term} />
        ))}
      </Box>
    </Box>
  );
};

// ============================================================================
// HTML GENERATORS
// ============================================================================

const generateHtml = (config: TemplateConfig): string => {
  const c = config;
  const pillRadius = c.borderRadius === 'pill' ? '50px' : c.borderRadius === 'square' ? '4px' : '8px';
  const pillPadding = c.pillSize === 'small' ? '6px 12px' : c.pillSize === 'large' ? '12px 20px' : '8px 16px';
  const pillFont = c.pillSize === 'small' ? '12px' : c.pillSize === 'large' ? '15px' : '13px';
  const pillColor = c.pillColor || '#2563eb';
  const textColorVal = c.textColor || '#ffffff';
  const hoverStyle = c.hoverStyle || 'darken';
  const pillsPerRow = c.pillsPerRow || 5;
  const isExpandable = Boolean(c.expandable);
  const viewMoreText = c.viewMoreText || 'Show More';

  let pillBg: string, pillTextColor: string, pillBorder: string;
  if (c.pillStyle === 'filled') {
    pillBg = pillColor as string;
    pillTextColor = textColorVal as string;
    pillBorder = 'none';
  } else if (c.pillStyle === 'outlined') {
    pillBg = 'transparent';
    pillTextColor = pillColor as string;
    pillBorder = `1.5px solid ${pillColor}`;
  } else {
    pillBg = 'transparent';
    pillTextColor = pillColor as string;
    pillBorder = 'none';
  }

  let hoverCss = '';
  if (hoverStyle === 'darken') {
    hoverCss =
      c.pillStyle === 'filled'
        ? `background:color-mix(in srgb, ${pillColor} 80%, black);`
        : `background:color-mix(in srgb, ${pillColor} 80%, black);color:${textColorVal};`;
  } else if (hoverStyle === 'lighten') {
    hoverCss =
      c.pillStyle === 'filled'
        ? `background:color-mix(in srgb, ${pillColor} 80%, white);`
        : `background:${pillColor}15;`;
  } else if (hoverStyle === 'invert') {
    hoverCss =
      c.pillStyle === 'filled'
        ? `background:transparent;color:${pillColor};border:1.5px solid ${pillColor};`
        : `background:${pillColor};color:${textColorVal};border:none;`;
  }

  const layoutStyle =
    c.layout === 'scroll'
      ? 'display:flex;gap:8px;overflow-x:auto;'
      : c.layout === 'grid'
      ? `display:grid;grid-template-columns:repeat(${c.columns || 3},1fr);gap:8px;`
      : 'display:flex;flex-wrap:wrap;gap:8px;';

  const moduleId = '{{moduleId}}';
  const expandableScript = isExpandable
    ? `
<script>
(function(){
  var container = document.querySelector('[data-module-id="${moduleId}"]');
  if (!container) return;
  var pills = container.querySelectorAll('.quattr-pill');
  var viewMoreBtn = container.querySelector('.quattr-view-more-btn');
  var expanded = false;
  var pillsPerRow = ${pillsPerRow};

  function updateVisibility() {
    pills.forEach(function(pill, i) {
      pill.style.display = (expanded || i < pillsPerRow) ? '' : 'none';
    });
    if (viewMoreBtn) {
      viewMoreBtn.textContent = expanded ? '← Show Less' : '${viewMoreText}';
    }
  }

  if (viewMoreBtn) {
    viewMoreBtn.addEventListener('click', function(e) {
      e.preventDefault();
      expanded = !expanded;
      updateVisibility();
    });
  }
  updateVisibility();
})();
</script>`
    : '';

  const containerPad =
    c.containerPadding === 'custom' && c.customPadding
      ? (c.customPadding as string)
      : getPadding((c.containerPadding as string) || 'md');

  const customCss = (c.customCss as string) || '';

  return `<style>
.quattr-related-searches .quattr-pill:hover { ${hoverCss}text-decoration:none !important; }
${customCss}
</style>
<div class="quattr-module quattr-related-searches${
    c.customCssClass ? ' ' + c.customCssClass : ''
  }" data-module-id="{{moduleId}}" style="background:${
    c.backgroundColor || '#f8fafc'
  };padding:${containerPad};font-family:system-ui,-apple-system,sans-serif;border-radius:8px;">
  ${
    c.showHeading
      ? `<div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">
    ${
      c.headingStyle === 'with-icon'
        ? '<svg style="width:18px;height:18px;color:#666;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
        : ''
    }
    <h4 style="margin:0;flex:1;font-size:${c.headingStyle === 'minimal' ? '13px' : '15px'};font-weight:${
          c.headingStyle === 'minimal' ? '500' : '600'
        };color:${c.headingStyle === 'minimal' ? '#666' : '#1a1a2e'};${
          c.headingStyle === 'minimal' ? 'text-transform:uppercase;letter-spacing:0.5px;' : ''
        }">${c.headingText || 'Related Searches'}</h4>
    ${
      isExpandable
        ? `<a href="#" class="quattr-view-more-btn" style="font-size:13px;color:${pillColor};text-decoration:none;font-weight:500;">${viewMoreText}</a>`
        : ''
    }
  </div>`
      : ''
  }
  <div style="${layoutStyle}">
    {{#each links}}
    {{#if (lt @index ${c.maxPills || 10})}}
    <a href="{{url}}" class="quattr-pill" style="display:inline-flex;align-items:center;gap:6px;padding:${pillPadding};border-radius:${pillRadius};font-size:${pillFont};font-weight:500;text-decoration:none;white-space:nowrap;background:${pillBg};color:${pillTextColor};border:${pillBorder};transition:all 0.2s ease;">
      ${
        c.showSearchIcon
          ? '<svg style="width:14px;height:14px;flex-shrink:0;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
          : ''
      }
      <span>{{title}}</span>
    </a>
    {{/if}}
    {{/each}}
  </div>
</div>${expandableScript}`;
};

const generatePreviewHtml = (config: TemplateConfig): string => {
  const c = config;
  const maxPills = Math.min((c.maxPills as number) || 10, 10);
  const pillsPerRow = (c.pillsPerRow as number) || 5;
  const isExpandable = Boolean(c.expandable);
  const viewMoreText = (c.viewMoreText as string) || 'Show More';
  const pillRadius = c.borderRadius === 'pill' ? '50px' : c.borderRadius === 'square' ? '4px' : '8px';
  const pillPadding = c.pillSize === 'small' ? '6px 12px' : c.pillSize === 'large' ? '12px 20px' : '8px 16px';
  const pillFont = c.pillSize === 'small' ? '12px' : c.pillSize === 'large' ? '15px' : '13px';
  const pillColor = (c.pillColor as string) || '#2563eb';
  const textColorVal = (c.textColor as string) || '#ffffff';
  const hoverStyle = (c.hoverStyle as string) || 'darken';

  let pillBg: string, pillTextColor: string, pillBorder: string;
  if (c.pillStyle === 'filled') {
    pillBg = pillColor;
    pillTextColor = textColorVal;
    pillBorder = 'none';
  } else if (c.pillStyle === 'outlined') {
    pillBg = 'transparent';
    pillTextColor = pillColor;
    pillBorder = `1.5px solid ${pillColor}`;
  } else {
    pillBg = 'transparent';
    pillTextColor = pillColor;
    pillBorder = 'none';
  }

  let hoverCss = '';
  if (hoverStyle === 'darken') {
    hoverCss = c.pillStyle === 'filled' ? 'filter:brightness(0.85);' : `background:${pillColor};color:${textColorVal};`;
  } else if (hoverStyle === 'lighten') {
    hoverCss = c.pillStyle === 'filled' ? 'filter:brightness(1.15);' : `background:${pillColor}15;`;
  } else if (hoverStyle === 'invert') {
    hoverCss =
      c.pillStyle === 'filled'
        ? `background:transparent;color:${pillColor};border:1.5px solid ${pillColor};`
        : `background:${pillColor};color:${textColorVal};border:none;`;
  }

  const layoutStyle =
    c.layout === 'scroll'
      ? 'display:flex;gap:8px;overflow-x:auto;'
      : c.layout === 'grid'
      ? `display:grid;grid-template-columns:repeat(${c.columns || 3},1fr);gap:8px;`
      : 'display:flex;flex-wrap:wrap;gap:8px;';

  const searchIcon = c.showSearchIcon
    ? '<svg style="width:14px;height:14px;flex-shrink:0;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
    : '';

  const visiblePills = isExpandable ? pillsPerRow : maxPills;
  const hasMorePills = maxPills > pillsPerRow;

  const containerPad =
    c.containerPadding === 'custom' && c.customPadding
      ? (c.customPadding as string)
      : getPadding((c.containerPadding as string) || 'md');

  const customCssPreview = (c.customCss as string) || '';

  return `
    <style>.preview-pill:hover { ${hoverCss}text-decoration:none !important; }${
    customCssPreview ? '\n' + customCssPreview : ''
  }</style>
    <div style="background:${
      c.backgroundColor || '#f8fafc'
    };padding:${containerPad};font-family:system-ui,-apple-system,sans-serif;border-radius:8px;">
      ${
        c.showHeading
          ? `<div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">
        ${
          c.headingStyle === 'with-icon'
            ? '<svg style="width:18px;height:18px;color:#666;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></svg>'
            : ''
        }
        <h4 style="margin:0;flex:1;font-size:${c.headingStyle === 'minimal' ? '13px' : '15px'};font-weight:${
              c.headingStyle === 'minimal' ? '500' : '600'
            };color:${c.headingStyle === 'minimal' ? '#666' : '#1a1a2e'};${
              c.headingStyle === 'minimal' ? 'text-transform:uppercase;letter-spacing:0.5px;' : ''
            }">${c.headingText || 'Related Searches'}</h4>
        ${
          isExpandable && hasMorePills
            ? `<a href="#" style="font-size:13px;color:${pillColor};text-decoration:none;font-weight:500;">${viewMoreText} (${
                maxPills - (pillsPerRow as number)
              } more)</a>`
            : ''
        }
      </div>`
          : ''
      }
      <div style="${layoutStyle}">
        ${sampleData.searches
          .slice(0, visiblePills as number)
          .map(
            (term) => `
          <a href="#" class="preview-pill" style="display:inline-flex;align-items:center;gap:6px;padding:${pillPadding};border-radius:${pillRadius};font-size:${pillFont};font-weight:500;text-decoration:none;white-space:nowrap;background:${pillBg};color:${pillTextColor};border:${pillBorder};transition:all 0.2s ease;">
            ${searchIcon}
            <span>${term}</span>
          </a>
        `
          )
          .join('')}
      </div>
    </div>`;
};

// ============================================================================
// EXPORT MODULE
// ============================================================================

export const relatedSearchesTemplate: TemplateModule = {
  definition,
  Preview,
  generateHtml,
  generatePreviewHtml,
  sampleData,
};

export default relatedSearchesTemplate;
